import type { KeepDataFromQuoteMapping } from '@/features/Sparten/types';
import { isDate, parseISODate } from '@/utils/dates/dates';
import string from '@/utils/formatters/string';
import { number } from '@/utils/formatters/number';
import { getCurrentSpartenConfig } from '@/utils/getCurrentSpartenConfig/getCurrentSpartenConfig';
import type { Role } from '@/features/Clients/types';
import { OPTIONAL_ROLE } from '@/features/Clients/types';

export enum KEEP_DATA_FROM_QUOTE_MAPPING {
  BIRTHDAY = 'birthday',
  BANK_INFORMATION = 'bankInformation',
  ZIP_CODE = 'zipCode',
  ADDRESS = 'address',
  HEIGHT_AND_WEIGHT = 'heightAndWeight',
  FAMILIENSTAND = 'familienstand',
  TARIFGRUPPE = 'tarifgruppe',
  CHILDREN = 'anzahlKinder',
  COMMERCIAL_WORK_PERCENTAGE = 'anteilTaetigkeitBuero',
  SMOKER = 'nonSmoker',
  BERUFSGRUPPE = 'berufsgruppe',
  PERSONELL_RESPONSIBILITY = 'personalverantwortung',
  LEVEL_OF_EDUCATION = 'hoechsterBildungsabschluss',
  FUEHRERSCHEINDATUM = 'fuehrerschein',
  KFZ_FAHRER_META = 'kfzFahrerMeta',
}

export const keepDataFromQuoteMappings: {
  [key in KEEP_DATA_FROM_QUOTE_MAPPING]: KeepDataFromQuoteMapping[];
} = {
  [KEEP_DATA_FROM_QUOTE_MAPPING.BIRTHDAY]: [
    {
      path: 'geburtsdatum',
      formatter: (date: string | Date): Date => {
        if (isDate(date)) {
          return date;
        }

        return parseISODate(date);
      },
    },
  ],
  [KEEP_DATA_FROM_QUOTE_MAPPING.BANK_INFORMATION]: [
    {
      path: 'bankverbindung.bankname',
      formatter: string,
    },
    {
      path: 'bankverbindung.bic',
      formatter: string,
    },
    {
      path: 'bankverbindung.iban',
      formatter: string,
    },
  ],
  [KEEP_DATA_FROM_QUOTE_MAPPING.ZIP_CODE]: [
    {
      path: 'adresse.plz',
      formatter: string,
    },
  ],
  [KEEP_DATA_FROM_QUOTE_MAPPING.ADDRESS]: [
    {
      path: 'adresse.plz',
      formatter: string,
    },
    {
      path: 'adresse.strasse',
      formatter: string,
    },
    {
      path: 'adresse.hausnummer',
      formatter: string,
    },
    {
      path: 'adresse.ort',
      formatter: string,
    },
  ],
  [KEEP_DATA_FROM_QUOTE_MAPPING.HEIGHT_AND_WEIGHT]: [
    {
      path: 'groesse',
      formatter: number,
      skipIfQuoteValueIsOneOf: [undefined, null],
    },
    {
      path: 'gewicht',
      formatter: number,
      skipIfQuoteValueIsOneOf: [undefined, null],
    },
  ],
  [KEEP_DATA_FROM_QUOTE_MAPPING.FAMILIENSTAND]: [
    {
      path: 'familienstand',
      formatter: string,
    },
  ],
  [KEEP_DATA_FROM_QUOTE_MAPPING.TARIFGRUPPE]: [
    {
      path: 'tarifgruppe',
      formatter: string,
    },
  ],
  [KEEP_DATA_FROM_QUOTE_MAPPING.CHILDREN]: [
    {
      path: 'anzahlKinder',
      formatter: number,
      skipIfQuoteValueIsOneOf: [undefined, null],
    },
  ],
  [KEEP_DATA_FROM_QUOTE_MAPPING.COMMERCIAL_WORK_PERCENTAGE]: [
    {
      path: 'anteilTaetigkeitBuero',
      formatter: number,
    },
  ],
  [KEEP_DATA_FROM_QUOTE_MAPPING.SMOKER]: [
    {
      path: 'nichtraucher',
      formatter: string,
    },
  ],
  [KEEP_DATA_FROM_QUOTE_MAPPING.BERUFSGRUPPE]: [
    {
      path: 'berufsgruppe',
      formatter: string,
    },
  ],
  [KEEP_DATA_FROM_QUOTE_MAPPING.PERSONELL_RESPONSIBILITY]: [
    {
      path: 'personalverantwortung',
      formatter: number,
    },
  ],
  [KEEP_DATA_FROM_QUOTE_MAPPING.LEVEL_OF_EDUCATION]: [
    {
      path: 'hoechsterBildungsabschluss',
      formatter: string,
    },
  ],
  [KEEP_DATA_FROM_QUOTE_MAPPING.FUEHRERSCHEINDATUM]: [
    {
      path: 'fuehrerscheindatum',
      formatter: (date: string | Date): Date => {
        if (isDate(date)) {
          return date;
        }

        return parseISODate(date);
      },
    },
  ],
  [KEEP_DATA_FROM_QUOTE_MAPPING.KFZ_FAHRER_META]: [
    {
      path: 'meta',
      formatter: (
        quoteMeta: Record<string, unknown>,
      ): Record<string, unknown> => {
        const { clientEntityMetaSchema } = getCurrentSpartenConfig();

        let schemaDefaults = {};
        if (clientEntityMetaSchema) {
          const fahrerRoles = [
            OPTIONAL_ROLE.FAHRER_1,
            OPTIONAL_ROLE.FAHRER_2,
            OPTIONAL_ROLE.FAHRER_3,
            OPTIONAL_ROLE.FAHRER_4,
            OPTIONAL_ROLE.FAHRER_5,
          ];
          for (const role of fahrerRoles) {
            const metaSchema = clientEntityMetaSchema[role as Role];
            if (metaSchema) {
              schemaDefaults = metaSchema.getDefault();
              break;
            }
          }
        }

        return {
          ...schemaDefaults,
          ...(quoteMeta || {}),
        };
      },
    },
  ],
};
